import React, { useEffect, useState } from "react";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import Swal from "sweetalert2";
import { getLeaveRequests, approveLeave, deleteLeave } from "../../../services/attendance";

const isAdmin = localStorage.getItem("Role") === "1";
const isManager = localStorage.getItem("Role") === "2";
const canApprove = isAdmin || isManager;

export const LeaveApprovals = () => {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [show, setShow] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [modalAction, setModalAction] = useState("approve");
  const [modalLeaveType, setModalLeaveType] = useState("leave");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadList(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadList = async (filterStatus) => {
    const res = await getLeaveRequests({ status: filterStatus });
    if (res && res.status === true) {
      setRows(res.data.requests.data);
    }
  };

  const handleaction = (row, action) => {
    setModalRow(row);
    setModalAction(action);
    setModalLeaveType(row.is_lop ? "lop" : "leave");
    setRemarks(row.remarks || "");
    setShow(true);
  };

  const submitAction = async () => {
    if (!modalRow) return;
    setSubmitting(true);
    const res = await approveLeave({
      id: modalRow.id,
      action: modalAction,
      remarks: remarks || "",
      leave_type: modalLeaveType,
    });
    setSubmitting(false);
    if (res) {
      res.status === true ? toast.success(res.message) : toast.info(res.message);
      setShow(false);
      loadList(statusFilter);
    }
  };

  const badge = (value) => {
    if (value === "approved") return "badge bg-success";
    if (value === "rejected") return "badge bg-danger";
    return "badge bg-warning";
  };

  const columnDefs = [
    {
      headerName: "S.No",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      sortable: false,
      filter: false,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "Staff",
      field: "user.name",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "From Date",
      field: "from_date",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      cellStyle: { textAlign: "left" },
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "-",
    },
    {
      headerName: "To Date",
      field: "to_date",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      cellStyle: { textAlign: "left" },
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "-",
    },
    {
      headerName: "Days",
      width: 80,
      cellStyle: { textAlign: "center" },
      valueGetter: (params) => {
        if (!params.data.from_date || !params.data.to_date) return "-";
        const from = moment(params.data.from_date);
        const to = moment(params.data.to_date);
        return to.diff(from, "days") + 1;
      },
    },
    {
      headerName: "Reason",
      field: "reason",
      sortable: false,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 180,
      cellStyle: { textAlign: "left" },
      cellRenderer: (params) => {
        const text = params.value || "-";
        return text.length > 50 ? text.substring(0, 50) + "..." : text;
      },
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 110,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params) => (
        <span className={badge(params.value)}>{params.value}</span>
      ),
    },
    {
      headerName: "Leave Type / LOP",
      field: "is_lop",
      width: 140,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params) => (
        <div>
          {params.value ? (
            <span className="badge bg-danger me-1">LOP</span>
          ) : (
            <span className="badge bg-info me-1">Leave</span>
          )}
          {canApprove && (
            <button
              className="btn btn-sm btn-danger"
              style={{ padding: "1px 5px", fontSize: "11px" }}
              title="Change Leave Type"
              onClick={() => handleaction(params.data, "update_type")}
            >
              <i className="fa fa-edit"></i> Edit
            </button>
          )}
        </div>
      ),
    },
    {
      headerName: "Approved By",
      field: "approver.name",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      cellStyle: { textAlign: "left" },
      valueFormatter: (params) => params.value || "-",
    },
    {
      headerName: "Remarks",
      field: "remarks",
      sortable: false,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      cellStyle: { textAlign: "left" },
      valueFormatter: (params) => params.value || "-",
    },
    ...(canApprove
      ? [
          {
            headerName: "Approve/Reject",
            field: "id",
            width: 200,
            sortable: false,
            filter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
              if (params.data.status !== "pending") {
                return (
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleaction(params.data, "update_type")}
                  >
                    <i className="fa fa-pencil-alt me-1"></i>Change Type
                  </button>
                );
              }
              return (
                <div>
                  <button
                    className="btn btn-sm btn-success me-1"
                    onClick={() => handleaction(params.data, "approve")}
                  >
                    <i className="fa fa-check me-1"></i>Approve
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleaction(params.data, "reject")}
                  >
                    <i className="fa fa-times me-1"></i>Reject
                  </button>
                </div>
              );
            },
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            headerName: "Delete",
            field: "id",
            width: 90,
            sortable: false,
            filter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
              return (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    Swal.fire({
                      text: "Delete this leave request?",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#ac0404",
                      cancelButtonColor: "#6259ca",
                      confirmButtonText: "Delete",
                      allowOutsideClick: false,
                    }).then(async (result) => {
                      if (result.isConfirmed) {
                        const res = await deleteLeave({ id: params.data.id });
                        if (res && res.status === true) {
                          toast.success(res.message);
                          loadList(statusFilter);
                        }
                      }
                    });
                  }}
                >
                  <i className="fa fa-trash"></i>
                </button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className="app-content main-content">
      <div className="side-app main-container">
        <div className="page-header d-xl-flex d-block">
          <div className="page-leftheader">
            <div className="page-title">Leave Approvals</div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Leave Requests</h4>
                <div className="ms-auto" style={{ width: "200px" }}>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="card-body">
                <div className="ag-theme-alpine ag-style mt-2">
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rows}
                    pagination="true"
                    paginationPageSize="10"
                    sortingOrder={["asc", "desc"]}
                    suppressDragLeaveHidesColumns={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <h4>
            {modalAction === "approve"
              ? "Approve Leave Request"
              : modalAction === "reject"
              ? "Reject Leave Request"
              : "Change Leave Type"}
          </h4>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <p className="mb-3">
                {modalAction === "approve"
                  ? "Approve leave for "
                  : modalAction === "reject"
                  ? "Reject leave for "
                  : "Change leave type for "}
                <strong>
                  {modalRow && modalRow.user
                    ? modalRow.user.name
                    : "this staff"}
                </strong>
                {modalRow ? (
                  <>
                    {" "}
                    from{" "}
                    <strong>
                      {moment(modalRow.from_date).format("DD/MM/YYYY")}
                    </strong>{" "}
                    to{" "}
                    <strong>
                      {moment(modalRow.to_date).format("DD/MM/YYYY")}
                    </strong>
                  </>
                ) : null}
                ?
              </p>
              <p>
                <small className="text-muted">
                  Reason: {modalRow?.reason || "-"}
                </small>
              </p>

              {modalAction !== "reject" && (
                <div className="mb-3">
                  <label className="form-label">
                    Leave Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={modalLeaveType}
                    onChange={(e) => setModalLeaveType(e.target.value)}
                  >
                    <option value="leave">Leave (Normal Paid Leave)</option>
                    <option value="lop">LOP (Loss of Pay)</option>
                    <option value="absent">Absent</option>
                  </select>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Remarks (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className={
              modalAction === "approve"
                ? "btn btn-success"
                : modalAction === "reject"
                ? "btn btn-danger"
                : "btn btn-primary"
            }
            onClick={submitting ? undefined : submitAction}
            disabled={submitting}
          >
            {submitting
              ? "Processing..."
              : modalAction === "approve"
              ? "Approve"
              : modalAction === "reject"
              ? "Reject"
              : "Save Leave Type"}
          </button>
          <button className="btn btn-secondary" onClick={() => setShow(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeaveApprovals;
